import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export interface Memo {
  id: string;
  title: string;
  content: string;
  category: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

const CATEGORIES = ['general', 'hygiene', 'preparation', 'procedure', 'securite', 'materiel'] as const;
export type MemoCategory = typeof CATEGORIES[number];
export { CATEGORIES as MEMO_CATEGORIES };

export function useMemos() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['memos', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Memo[]> => {
      const { data, error } = await supabase
        .from('memos')
        .select('*')
        .eq('owner_id', user!.id)
        .order('pinned', { ascending: false })
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data || []) as Memo[];
    },
  });

  const createMemo = useMutation({
    mutationFn: async (data: { title: string; content: string; category: string }) => {
      const { error } = await supabase.from('memos').insert({ owner_id: user!.id, ...data });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memos'] }),
  });

  const updateMemo = useMutation({
    mutationFn: async (data: { id: string; title?: string; content?: string; category?: string; pinned?: boolean }) => {
      const { id, ...updates } = data;
      const { error } = await supabase.from('memos').update(updates).eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memos'] }),
  });

  const deleteMemo = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('memos').delete().eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memos'] }),
  });

  return { ...query, memos: query.data || [], createMemo, updateMemo, deleteMemo };
}
